import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { useCart, eur } from "@/context/CartContext";
import { ShoppingCart, Loader2, Check, MessageCircle, Sparkles } from "lucide-react";
import InlineCTA from "@/components/InlineCTA";

function ProductCard({ p, added, onAdd }) {
    return (
        <div data-testid={`product-card-${p.slug}`} className="group rounded-2xl border border-border bg-white overflow-hidden flex flex-col transition hover:shadow-xl hover:-translate-y-0.5">
            <Link to={`/loja/${p.slug}`} data-testid={`product-link-${p.slug}`} className="block">
                <div className="aspect-[4/3] bg-muted overflow-hidden">
                    {p.image_url ? (
                        <img src={mediaUrl(p.image_url)} alt={p.name} className="h-full w-full object-cover transition group-hover:scale-105" />
                    ) : (
                        <div className="h-full w-full flex items-center justify-center text-muted-foreground"><ShoppingCart className="h-10 w-10" /></div>
                    )}
                </div>
            </Link>
            <div className="p-5 flex flex-col flex-1">
                <Link to={`/loja/${p.slug}`} className="hover:text-secondary">
                    <h3 className="font-heading text-lg font-bold text-primary transition">{p.name}</h3>
                </Link>
                {p.description ? <p className="mt-1 text-sm text-muted-foreground line-clamp-3 flex-1">{p.description}</p> : <div className="flex-1" />}
                <div className="mt-4 flex items-center justify-between">
                    <div className="flex items-baseline gap-1.5">
                        <span className="font-heading text-xl font-extrabold text-primary">{eur(p.price_cents)}</span>
                        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">s/IVA</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Link
                            to={`/loja/${p.slug}`}
                            data-testid={`view-product-${p.slug}`}
                            className="hidden sm:inline-flex items-center gap-1 rounded-full border border-border bg-white px-3 py-1.5 text-xs font-bold text-primary hover:border-primary/40 hover:bg-muted transition"
                        >
                            Ver
                        </Link>
                        <button
                            type="button"
                            onClick={() => onAdd(p)}
                            data-testid={`add-to-cart-${p.slug}`}
                            className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-4 py-2 text-sm font-bold text-secondary-foreground transition hover:-translate-y-0.5"
                        >
                            {added === p.id ? <Check className="h-4 w-4" /> : <ShoppingCart className="h-4 w-4" />}
                            {added === p.id ? "Adicionado" : "Adicionar"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function Loja() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [added, setAdded] = useState(null);
    const { add } = useCart();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/products");
                setProducts(data);
            } catch {
                /* ignore */
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const models = products.filter((p) => p.category === "model");
    const accessories = products.filter((p) => p.category === "accessory");

    const handleAdd = (p) => {
        add(p);
        setAdded(p.id);
        setTimeout(() => setAdded(null), 1200);
    };

    return (
        <div className="bg-background" data-testid="loja-page">
            <section className="container-x pt-6 pb-4">
                <span className="inline-block rounded-full bg-secondary/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-primary">Loja NOVIFOOD</span>
                <h1 className="mt-2 display-xl text-primary">Compre o seu equipamento</h1>
                <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Modelos e acessórios NOVIFOOD, prontos a encomendar.</p>
            </section>

            {loading ? (
                <div className="container-x py-16 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : products.length === 0 ? (
                <div className="container-x py-16 text-center text-muted-foreground">Ainda não há produtos disponíveis.</div>
            ) : (
                <div className="container-x pb-12 space-y-8">
                    {models.length > 0 && (
                        <div>
                            <h2 className="font-heading text-2xl font-bold text-primary mb-5">Modelos</h2>
                            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                                {models.map((p) => (
                                    <ProductCard key={p.id} p={p} added={added} onAdd={handleAdd} />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Inline CTA — indecisão → simulador */}
                    <InlineCTA
                        eyebrow="Não sabe qual escolher?"
                        title="Configure o seu carrinho e receba estimativa em minutos."
                        to="/simulador"
                        label="Abrir simulador"
                        variant="gold"
                        icon={Sparkles}
                        testId="loja-inline-cta-simulador"
                    />

                    {accessories.length > 0 && (
                        <div>
                            <h2 className="font-heading text-2xl font-bold text-primary mb-5">Acessórios</h2>
                            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                                {accessories.map((p) => (
                                    <ProductCard key={p.id} p={p} added={added} onAdd={handleAdd} />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Inline CTA — pós-acessórios → contacto */}
                    <InlineCTA
                        eyebrow="Precisa de ajuda?"
                        title="Fale com um especialista e receba uma proposta à medida."
                        to="/contacto"
                        label="Pedir orçamento"
                        variant="navy"
                        icon={MessageCircle}
                        testId="loja-inline-cta-contacto"
                    />
                </div>
            )}
        </div>
    );
}
